import { useEffect, useState } from "react";
import { Download, Share, PlusSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let deferred = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    listeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    listeners.forEach((fn) => fn(false));
  });
}

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const Steps = () => (
  <ol className="space-y-3 text-sm text-slate-700" data-testid="install-instructions">
    {isIOS() ? (
      <>
        <li className="flex gap-3"><Share className="h-5 w-5 shrink-0 text-blue-700" /> Tap the <b>Share</b> button in Safari.</li>
        <li className="flex gap-3"><PlusSquare className="h-5 w-5 shrink-0 text-blue-700" /> Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.</li>
      </>
    ) : (
      <>
        <li><b>Chrome / Edge / Samsung Internet (Android):</b> open the browser menu (⋮) and tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
        <li><b>Chrome / Edge (computer):</b> click the install icon at the right of the address bar, or menu → <b>Install Comfort Taxi</b>.</li>
        <li><b>Safari (iPhone/iPad):</b> Share → <b>Add to Home Screen</b>.</li>
        <li><b>Firefox (Android):</b> menu → <b>Install</b>.</li>
      </>
    )}
  </ol>
);

export const InstallAppButton = ({ className = "", label = "Install app" }) => {
  const [canPrompt, setCanPrompt] = useState(Boolean(deferred));
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setInstalled(isStandalone());
    listeners.add(setCanPrompt);
    return () => {
      listeners.delete(setCanPrompt);
    };
  }, []);
  if (installed) return null;
  const click = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      deferred = null;
      setCanPrompt(false);
    } else {
      setOpen(true);
    }
  };
  return (
    <>
      <button data-testid="install-app-button" data-can-prompt={canPrompt} onClick={click} className={`inline-flex items-center gap-2 ${className}`}>
        <Download className="h-4 w-4" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Install Comfort Taxi on your device</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Get the app on your home screen — it opens full-screen like a normal app, no app store needed.</p>
          <Steps />
        </DialogContent>
      </Dialog>
    </>
  );
};
